import React, { useState } from "react";
import cashCarImage from "../../assets/cashOnCar.png";
import styles from "./CashOnCar.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faUpload } from "@fortawesome/free-solid-svg-icons";

function CashOnCar() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: "",
    mobileNumber: "",
    email: "",
    gender: "Male",
    date: "",
    address: "",
    pincode: "",
    carRegistrationNo: "",
    panCard: "",
    loanDuration: "",
    employmentDetails: "",
  });
  const [files, setFiles] = useState([]);
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  // Validation functions
  const validateFullName = (value) => {
    if (!value) return "Full name is required";
    if (!/^[a-zA-Z\s]+$/.test(value)) return "Full name should contain only letters and spaces";
    return "";
  };

  const validateMobileNumber = (value) => {
    if (!value) return "Mobile number is required";
    if (!/^\d{10}$/.test(value)) return "Mobile number must be exactly 10 digits";
    return "";
  };

  const validateEmail = (value) => {
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email format";
    return "";
  };

  const validateDate = (value) => {
    if (!value) return "Date of birth is required";
    const today = new Date();
    const birthDate = new Date(value);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    if (age < 18) return "You must be at least 18 years old";
    return "";
  };

  const validateAddress = (value) => {
    if (!value) return "Address is required";
    if (!/^[a-zA-Z0-9\s,.-]+$/.test(value)) return "Address contains invalid characters";
    return "";
  };

  const validatePincode = (value) => {
    if (!value) return "Pincode is required";
    if (!/^\d{6}$/.test(value)) return "Pincode must be exactly 6 digits";
    return "";
  };

  const validateCarRegistrationNo = (value) => {
    if (!value) return "Car registration number is required";
    if (!/^[A-Z]{2}\d{2}[A-Z]{1,2}\d{1,4}$/.test(value)) return "Invalid registration number format (e.g., XX12XX1234)";
    return "";
  };

  const validatePanCard = (value) => {
    if (!value) return "PAN card number is required";
    if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(value)) return "Invalid PAN card format (e.g., ABCDE1234F)";
    return "";
  };

  const validateLoanDuration = (value) => {
    if (!value) return "Loan duration is required";
    const num = Number(value);
    if (isNaN(num) || num <= 0 || num > 240) return "Loan duration must be between 1 and 240 months";
    return "";
  };

  const validateEmploymentDetails = (value) => {
    if (!value) return "Employment details are required";
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Real-time validation
    let error = "";
    switch (name) {
      case "fullName":
        error = validateFullName(value);
        break;
      case "mobileNumber":
        error = validateMobileNumber(value);
        break;
      case "email":
        error = validateEmail(value);
        break;
      case "date":
        error = validateDate(value);
        break;
      case "address":
        error = validateAddress(value);
        break;
      case "pincode":
        error = validatePincode(value);
        break;
      case "carRegistrationNo":
        error = validateCarRegistrationNo(value);
        break;
      case "panCard":
        error = validatePanCard(value);
        break;
      case "loanDuration":
        error = validateLoanDuration(value);
        break;
      case "employmentDetails":
        error = validateEmploymentDetails(value);
        break;
      default:
        break;
    }
    setErrors({ ...errors, [name]: error });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    setErrors({ ...errors, files: "" });
  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, index) => index !== indexToRemove));
  };

  const validateStep = () => {
    const newErrors = {};
    if (currentStep === 1) {
      newErrors.fullName = validateFullName(formData.fullName);
      newErrors.mobileNumber = validateMobileNumber(formData.mobileNumber);
      newErrors.email = validateEmail(formData.email);
      newErrors.date = validateDate(formData.date);
      newErrors.address = validateAddress(formData.address);
      newErrors.pincode = validatePincode(formData.pincode);
    } else if (currentStep === 2) {
      newErrors.carRegistrationNo = validateCarRegistrationNo(formData.carRegistrationNo);
      newErrors.panCard = validatePanCard(formData.panCard);
      newErrors.loanDuration = validateLoanDuration(formData.loanDuration);
    } else if (currentStep === 3) {
      newErrors.employmentDetails = validateEmploymentDetails(formData.employmentDetails);
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        setShowDocUpload(true);
      }
    }
  };

  const handleBack = () => {
    if (showDocUpload) {
      setShowDocUpload(false);
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      setErrors({ ...errors, files: "Please upload at least one document" });
      return;
    }

    const token = localStorage.getItem("token");
    const vehicle_id = localStorage.getItem("vehicle_id");

    if (!token) {
      setErrors({ ...errors, general: "Authentication token missing. Please log in again." });
      return;
    }
    if (!vehicle_id) {
      setErrors({ ...errors, general: "Vehicle ID missing. Please select a vehicle." });
      return;
    }

    setLoading(true);

    const headers = new Headers();
    headers.append("Authorization", `Bearer ${token}`);

    const form = new FormData();
    form.append("service_type", "CashOnCar");
    form.append("vehicle_id", vehicle_id);
    form.append("additional_info", JSON.stringify(formData));

    if (files.length > 0) {
      files.forEach((file) => {
        form.append("documents", file);
      });
    }

    try {
      const response = await fetch(
        "https://api.rtoxpert.com/api/service-requests",
        {
          method: "POST",
          headers,
          body: form,
          redirect: "follow",
        }
      );

      if (response.ok) {
        setShowPopup(true);
        setErrors({});
        setTimeout(() => {
          setShowPopup(false);
          window.location.href = "/profile";
        }, 2000);
      } else {
        const errorData = await response.json().catch(() => null);
        setErrors({ ...errors, general: errorData?.message || "Submission failed. Please try again." });
      }
    } catch (err) {
      console.error(err);
      setErrors({ ...errors, general: "Connection error. Please check your internet connection." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.formSection}>
        {/* Progress indicator */}
        <div className={styles.stepsIndicator}>
          <div className={`${styles.stepCircle} ${currentStep >= 1 || showDocUpload ? styles.activeStep : ''}`}>
            1
          </div>
          <div className={`${styles.stepCircle} ${currentStep >= 2 || showDocUpload ? styles.activeStep : ''}`}>
            2
          </div>
          <div className={`${styles.stepCircle} ${currentStep >= 3 || showDocUpload ? styles.activeStep : ''}`}>
            3
          </div>
          <div className={`${styles.stepCircle} ${showDocUpload ? styles.activeStep : ''}`}>
            4
          </div>
        </div>

        {!showDocUpload ? (
          <>
            {currentStep === 1 && (
              <>
                <h2 className={styles.formTitle}>Personal Details</h2>
                <p className={styles.formSubtitle}>These details will be used for further communication</p>

                <div className={styles.inputGroup}>
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Enter Full Name"
                    className={styles.inputField}
                    value={formData.fullName}
                    onChange={handleChange}
                  />
                  {errors.fullName && <p className={styles.errorText}>{errors.fullName}</p>}
                </div>

                <div className={styles.inputGroup}>
                  <label>Mobile Number</label>
                  <input
                    type="tel"
                    name="mobileNumber"
                    placeholder="Enter Mobile Number"
                    className={styles.inputField}
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    maxLength="10"
                  />
                  {errors.mobileNumber && <p className={styles.errorText}>{errors.mobileNumber}</p>}
                </div>

                <div className={styles.inputGroup}>
                  <label>Email ID</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter Email ID"
                    className={styles.inputField}
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && <p className={styles.errorText}>{errors.email}</p>}
                </div>

                <div className={styles.inputGroup}>
                  <label>Gender</label>
                  <div className={styles.genderOptions}>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="gender"
                        value="Male"
                        checked={formData.gender === "Male"}
                        onChange={handleChange}
                        style={{ display: "none" }}
                      />
                      <span className={formData.gender === "Male" ? styles.radioChecked : styles.radioUnchecked}>
                        {formData.gender === "Male" && (
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </span>
                      <span>Male</span>
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="gender"
                        value="Female"
                        checked={formData.gender === "Female"}
                        onChange={handleChange}
                        style={{ display: "none" }}
                      />
                      <span className={formData.gender === "Female" ? styles.radioChecked : styles.radioUnchecked}>
                        {formData.gender === "Female" && (
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </span>
                      <span>Female</span>
                    </label>
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    name="date"
                    className={styles.inputField}
                    value={formData.date}
                    onChange={handleChange}
                    max={new Date().toISOString().split("T")[0]}
                  />
                  {errors.date && <p className={styles.errorText}>{errors.date}</p>}
                </div>

                <div className={styles.inputGroup}>
                  <label>Address</label>
                  <input
                    type="text"
                    name="address"
                    placeholder="Enter Address"
                    className={styles.inputField}
                    value={formData.address}
                    onChange={handleChange}
                  />
                  {errors.address && <p className={styles.errorText}>{errors.address}</p>}
                </div>

                <div className={styles.inputGroup}>
                  <label>Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    placeholder="Enter Pincode"
                    className={styles.inputField}
                    value={formData.pincode}
                    onChange={handleChange}
                    maxLength="6"
                  />
                  {errors.pincode && <p className={styles.errorText}>{errors.pincode}</p>}
                </div>
              </>
            )}

            {currentStep === 2 && (
              <>
                <h2 className={styles.formTitle}>Financial Details</h2>
                <p className={styles.formSubtitle}>These details will be used for further communication</p>

                <div className={styles.inputGroup}>
                  <label>Car Registration No.</label>
                  <input
                    type="text"
                    name="carRegistrationNo"
                    placeholder="Enter Car Registration No."
                    className={styles.inputField}
                    value={formData.carRegistrationNo}
                    onChange={handleChange}
                  />
                  {errors.carRegistrationNo && <p className={styles.errorText}>{errors.carRegistrationNo}</p>}
                </div>

                <div className={styles.inputGroup}>
                  <label>PAN Card</label>
                  <input
                    type="text"
                    name="panCard"
                    placeholder="Enter PAN Card Number"
                    className={styles.inputField}
                    value={formData.panCard}
                    onChange={handleChange}
                    maxLength="10"
                  />
                  {errors.panCard && <p className={styles.errorText}>{errors.panCard}</p>}
                </div>

                <div className={styles.inputGroup}>
                  <label>Duration of Loan (Months)</label>
                  <input
                    type="number"
                    name="loanDuration"
                    placeholder="Enter Loan Duration"
                    className={styles.inputField}
                    value={formData.loanDuration}
                    onChange={handleChange}
                    min="1"
                    max="240"
                  />
                  {errors.loanDuration && <p className={styles.errorText}>{errors.loanDuration}</p>}
                </div>
              </>
            )}

            {currentStep === 3 && (
              <>
                <h2 className={styles.formTitle}>Employment Details</h2>
                <p className={styles.formSubtitle}>These details will be used for further communication</p>

                <div className={styles.inputGroup}>
                  <label>Employment Details</label>
                  <div className={styles.selectWrapper}>
                    <select
                      name="employmentDetails"
                      className={styles.inputField}
                      value={formData.employmentDetails}
                      onChange={handleChange}
                    >
                      <option value="">Select Employment Type</option>
                      <option value="Salaried">Salaried</option>
                      <option value="Self-Employed">Self-Employed</option>
                      <option value="Unemployed">Unemployed</option>
                    </select>
                    <span className={styles.selectArrow}>
                      <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L6 6L11 1" stroke="#666666" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </span>
                  </div>
                  {errors.employmentDetails && <p className={styles.errorText}>{errors.employmentDetails}</p>}
                </div>
              </>
            )}

            <div className={styles.buttonGroup}>
              {currentStep > 1 && (
                <button className={styles.backButton} onClick={handleBack}>
                  Back
                </button>
              )}
              <button className={styles.nextButton} onClick={handleNext}>
                {currentStep < 3 ? "Next" : "Proceed to Upload"}
              </button>
            </div>
          </>
        ) : (
          <div className={styles.docUploadContainer}>
            <h3 className={styles.formTitle}>Upload Required Documents</h3>
            <p className={styles.formSubtitle}>Please upload the necessary documents to proceed</p>

            <div className={styles.documentsList}>
              <div className={styles.documentsNeeded}>
                <h4>Documents Required:</h4>
                <div className={styles.documentItem}>
                  <div className={styles.checkIcon}>
                    <FontAwesomeIcon icon={faCheck} />
                  </div>
                  <p>Vehicle Registration Certificate (RC)</p>
                </div>
                <div className={styles.documentItem}>
                  <div className={styles.checkIcon}>
                    <FontAwesomeIcon icon={faCheck} />
                  </div>
                  <p>Income Proof (Salary Slips/Bank Statements)</p>
                </div>
                <div className={styles.documentItem}>
                  <div className={styles.checkIcon}>
                    <FontAwesomeIcon icon={faCheck} />
                  </div>
                  <p>ID Proof (Aadhar/PAN Card)</p>
                </div>
                <div className={styles.documentItem}>
                  <div className={styles.checkIcon}>
                    <FontAwesomeIcon icon={faCheck} />
                  </div>
                  <p>Address Proof</p>
                </div>
              </div>

              <div className={styles.fileUploadArea}>
                <div className={styles.fileInputGroup}>
                  <div className={styles.fileUploadWrapper}>
                    <label className={styles.fileUploadLabel}>
                      <FontAwesomeIcon icon={faUpload} className={styles.uploadIcon} />
                      Choose File(s)
                      <input
                        type="file"
                        className={styles.fileInput}
                        onChange={handleFileChange}
                        multiple
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>

                  {files.length > 0 && (
                    <ul className={styles.uploadedFiles}>
                      {files.map((file, index) => (
                        <li key={index} className={styles.fileItem}>
                          <span>📄 {file.name}</span>
                          <button
                            className={styles.removeFileBtn}
                            onClick={() => removeFile(index)}
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {errors.files && <p className={styles.errorText}>{errors.files}</p>}
                </div>

                <div className={styles.buttonGroup}>
                  <button className={styles.backButton} onClick={handleBack}>
                    Back
                  </button>
                  <button
                    className={styles.submitButton}
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </div>
            </div>
            {errors.general && <p className={styles.errorText}>{errors.general}</p>}
          </div>
        )}

        {showPopup && (
          <div className={styles.popup}>
            <p>
              Cash on Car application submitted successfully! Our representative will contact you shortly. Redirecting to profile...
            </p>
          </div>
        )}
      </div>

      <div className={styles.imageSection}>
        <img
          src={cashCarImage}
          alt="Car illustration for cash on car"
          className={styles.vehicleImage}
        />
      </div>
    </div>
  );
}

export default CashOnCar;